<template>
  <div>
    <!-- Step 1: QR + 코드 입력 -->
    <form v-if="step === 'qr'" onSubmit="return false;" class="form">
      <h5 class="mb-2">TOTP 등록 (TOTP Enrollment)</h5>
      <p class="text-muted small">
        Google Authenticator 또는 Microsoft Authenticator 앱에서 아래 QR 코드를 스캔하세요.<br/>
        Scan the QR code below in Google / Microsoft Authenticator.
      </p>

      <div class="text-center my-3" v-if="qr_base64">
        <img :src="'data:image/png;base64,' + qr_base64" alt="TOTP QR" class="qr-img"/>
      </div>

      <div class="form-group">
        <button type="button" class="btn-secret-toggle" @click="show_secret = !show_secret">
          {{ show_secret ? '시크릿 숨기기 / Hide secret' : '시크릿 텍스트 보기 / Show secret' }}
        </button>
        <div v-if="show_secret" class="secret-box">{{ secret_text }}</div>
      </div>

      <div class="form-group">
        <label for="code">앱에서 표시된 6자리 코드 / 6-digit code</label>
        <input type="text" id="code" ref="code" class="input" placeholder="123456"
               v-model.trim="code" autocomplete="off" maxlength="6"
               @keyup.enter="onConfirm"/>
      </div>

      <button type="button" class="btn-reset" @click="onConfirm" :disabled="busy">
        확인 / Verify &amp; Enroll
      </button>

      <!-- 그레이스 기간 중일 때만 노출 -->
      <button v-if="optional" type="button" class="btn-skip" @click="onSkip" :disabled="busy">
        다음에 등록 / Skip for now
      </button>

      <div class="text-muted small mt-2" v-if="errorMsg">{{ errorMsg }}</div>
      <div class="text-muted xsmall mt-2" v-if="optional">
        💡 그레이스 기간 중에는 등록을 미룰 수 있습니다. 보안 강화를 위해 가능한 빨리 등록을 권장합니다.<br/>
        💡 You can skip during the grace period. We recommend enrolling soon for better security.
      </div>
    </form>

    <!-- Step 2: 백업 코드 1회 표시 -->
    <div v-else-if="step === 'recovery'" class="recovery-section">
      <h5 class="recovery-title">🗝️ 백업 코드 / Recovery Codes</h5>
      <div class="recovery-warning">
        ⚠️ <strong>폰 분실 시에만 사용하는 1회용 코드입니다.</strong> 이 화면을 닫으면 다시 볼 수 없으니 반드시 저장하세요.<br/>
        ⚠️ <strong>One-time codes — used only when your phone is lost.</strong> You won't see them again, save them now.
      </div>

      <div class="recovery-codes-box">
        <div v-for="(c, i) in recovery_codes" :key="i" class="recovery-code-item">
          <span class="code-index">{{ String(i + 1).padStart(2, '0') }}</span>
          <span class="code-value">{{ c }}</span>
        </div>
      </div>

      <div class="save-prompt">📥 코드를 안전한 곳에 저장하세요 / Save the codes securely</div>
      <div class="action-buttons">
        <button type="button" class="btn-action btn-download" @click="onDownload">
          <span class="btn-icon">⬇</span>
          <span class="btn-text">다운로드<br/><small>Download</small></span>
        </button>
        <button type="button" class="btn-action btn-print" @click="onPrint">
          <span class="btn-icon">🖨</span>
          <span class="btn-text">인쇄<br/><small>Print</small></span>
        </button>
      </div>

      <label class="save-confirm">
        <input type="checkbox" v-model="saved_ack"/>
        <span>저장 완료했습니다 / I have saved them</span>
      </label>

      <button type="button" class="btn-continue" @click="onFinish" :disabled="!saved_ack || busy">
        {{ saved_ack ? '진입 / Continue ▶' : '저장 후 진입 가능 / Save first to continue' }}
      </button>
    </div>
  </div>
</template>

<script>
import { lfnEnrollTotp, lfnConfirmTotpEnrollment, lfnSkipTotpEnrollment, lfnRedirectAfterSuccess } from '@/Service/Login/totp';

export default {
  name: 'totpenroll',
  data() {
    return {
      step: 'loading',
      pending_token: '',
      next_token: '',
      qr_base64: '',
      otpauth_uri: '',
      secret_text: '',
      show_secret: false,
      code: '',
      errorMsg: '',
      busy: false,
      recovery_codes: [],
      saved_ack: false,
      session_data: null,
      sys_gbn: '',
      optional: false
    };
  },
  async mounted() {
    const params = new URL(window.location).searchParams;
    this.pending_token = params.get('token') || '';
    this.sys_gbn = params.get('sys_gbn') || '';
    this.optional = (params.get('optional') === '1');
    if (!this.pending_token) {
      alert('잘못된 접근입니다. / Invalid access.');
      window.location.href = '/';
      return;
    }
    this.busy = true;
    const r = await lfnEnrollTotp(this.pending_token);
    this.busy = false;
    if (r && r.qr_base64) {
      this.qr_base64 = r.qr_base64;
      this.otpauth_uri = r.otpauth_uri;
      this.secret_text = r.secret_text;
      this.next_token = r.next_token;
      this.step = 'qr';
      this.$nextTick(() => { if (this.$refs.code) this.$refs.code.focus(); });
    } else {
      alert((r && (r.resultMsg || r.errMsg)) || '등록 화면 진입에 실패했습니다.');
      window.location.href = '/';
    }
  },
  methods: {
    async onConfirm() {
      if (this.busy) return;
      if (!this.code || this.code.length !== 6) {
        this.errorMsg = '6자리 코드를 입력하세요.';
        return;
      }
      this.busy = true;
      this.errorMsg = '';
      const r = await lfnConfirmTotpEnrollment(this.next_token, this.code);
      this.busy = false;
      if (r && r.err === 0 && r.result === 'S') {
        this.recovery_codes = r.recovery_codes || [];
        this.session_data = r;
        this.step = 'recovery';
      } else if (r && r.result === 'F_TOTP_PENDING_EXPIRED') {
        alert('등록 시간이 만료되었습니다. 처음부터 다시 시도해주세요.');
        window.location.href = '/';
      } else {
        this.errorMsg = (r && (r.resultMsg || r.errMsg)) || '코드가 일치하지 않습니다.';
      }
    },
    onDownload() {
      const text = '# TOTP Recovery Codes (CMSH-996)\n' +
                   '# Generated: ' + new Date().toISOString() + '\n\n' +
                   this.recovery_codes.join('\n') + '\n';
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'totp-recovery-codes.txt';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    onPrint() {
      const w = window.open('', '_blank', 'width=400,height=600');
      w.document.write('<h3>TOTP Recovery Codes</h3><pre>' + this.recovery_codes.join('\n') + '</pre>');
      w.document.close(); w.focus(); w.print();
    },
    onFinish() {
      if (!this.session_data) { window.location.href = '/'; return; }
      const ok = lfnRedirectAfterSuccess(this.session_data, this.sys_gbn);
      if (!ok) window.location.href = '/';
    },
    async onSkip() {
      if (this.busy) return;
      if (!this.optional) return;   // 그레이스 종료 후 클라이언트 측 가드 (서버도 검증)
      if (!confirm('TOTP 등록을 미루고 이번 로그인을 진행하시겠습니까?\n다음 로그인 시 다시 안내됩니다.\n\nSkip TOTP enrollment for this sign-in? You will be prompted again next time.')) return;

      this.busy = true;
      this.errorMsg = '';
      const r = await lfnSkipTotpEnrollment(this.next_token);
      this.busy = false;

      if (r && r.err === 0 && r.result === 'S') {
        const ok = lfnRedirectAfterSuccess(r, this.sys_gbn);
        if (!ok) window.location.href = '/';
      } else if (r && r.result === 'F_TOTP_ENROLL') {
        alert('그레이스 기간이 종료되었습니다. TOTP 등록이 필요합니다.\nGrace period has ended. TOTP enrollment is required.');
      } else if (r && r.result === 'F_TOTP_PENDING_EXPIRED') {
        alert('등록 시간이 만료되었습니다. 처음부터 다시 시도해주세요.');
        window.location.href = '/';
      } else {
        this.errorMsg = (r && (r.resultMsg || r.errMsg)) || '스킵 처리에 실패했습니다.';
      }
    }
  }
};
</script>

<style scoped>
.qr-img { width: 220px; height: 220px; border: 1px solid #ddd; padding: 8px; background: #fff; }
.btn-secret-toggle { background: none; border: 1px solid #ddd; padding: 4px 10px; border-radius: 4px; font-size: 12px; cursor: pointer; }
.secret-box { font-family: monospace; background: #f7f7f7; border: 1px solid #ddd; padding: 8px; margin-top: 6px; word-break: break-all; }
.btn-reset { width: 100%; padding: 10px; background: #1a73e8; color: #fff; border: 0; border-radius: 4px; cursor: pointer; }
.btn-reset:disabled { background: #aaa; }
.btn-skip { width: 100%; padding: 8px; background: #fff; color: #666; border: 1px solid #ccc; border-radius: 4px; cursor: pointer; margin-top: 6px; font-size: 13px; }
.btn-skip:hover { background: #f5f5f5; }
.btn-skip:disabled { color: #aaa; border-color: #ddd; cursor: not-allowed; }
.xsmall { font-size: 11px; }
.text-warning { color: #d97706; }
.text-muted { color: #888; }
.small { font-size: 12px; }
.form-group { margin: 8px 0; }
.input { width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px; font-size: 18px; letter-spacing: 4px; text-align: center; }

/* ============================================================
   백업 코드 화면 (개선 디자인 — 버튼 시각화 강화)
   ============================================================ */
.recovery-section { padding: 4px 0; }

.recovery-title {
  font-size: 17px;
  font-weight: bold;
  color: #1a4480;
  margin: 0 0 12px 0;
}

.recovery-warning {
  background: #fff7ed;
  border-left: 4px solid #ea580c;
  padding: 12px 14px;
  border-radius: 0 6px 6px 0;
  font-size: 12.5px;
  color: #7a4500;
  line-height: 1.5;
  margin: 12px 0;
}

.recovery-codes-box {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  background: #f8faff;
  padding: 14px;
  border-radius: 8px;
  border: 1px solid #d0d7e2;
  margin: 12px 0;
}

.recovery-code-item {
  display: flex;
  align-items: center;
  background: #fff;
  padding: 8px 12px;
  border-radius: 5px;
  border: 1px solid #e0e6f0;
  font-family: 'Cascadia Code', Consolas, 'Courier New', monospace;
}

.code-index {
  font-size: 10px;
  color: #999;
  margin-right: 10px;
  min-width: 18px;
  font-family: sans-serif;
}

.code-value {
  font-weight: bold;
  color: #1a4480;
  letter-spacing: 1.5px;
  font-size: 14px;
}

.save-prompt {
  font-size: 13px;
  font-weight: bold;
  color: #1a4480;
  margin: 20px 0 8px 0;
}

.action-buttons {
  display: flex;
  gap: 10px;
  margin: 8px 0 16px 0;
}

.btn-action {
  flex: 1;
  padding: 14px 8px;
  background: linear-gradient(180deg, #ffffff 0%, #e8f0fe 100%);
  color: #1a4480;
  border: 2px solid #1a73e8;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.15s ease;
  box-shadow: 0 2px 6px rgba(26, 115, 232, 0.15);
  font-weight: bold;
}

.btn-action:hover {
  background: #1a73e8;
  color: white;
  box-shadow: 0 4px 12px rgba(26, 115, 232, 0.35);
  transform: translateY(-1px);
}

.btn-action:active {
  transform: translateY(0);
}

.btn-icon {
  font-size: 22px;
  line-height: 1;
}

.btn-text {
  font-size: 13px;
  text-align: left;
  line-height: 1.2;
}

.btn-text small {
  font-size: 10px;
  font-weight: normal;
  opacity: 0.75;
}

.save-confirm {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f5f8fc;
  border: 1.5px solid #c0cfdf;
  border-radius: 6px;
  padding: 12px 14px;
  margin: 14px 0;
  cursor: pointer;
  font-size: 13px;
  color: #333;
  user-select: none;
}

.save-confirm:hover {
  border-color: #1a73e8;
  background: #eef3fb;
}

.save-confirm input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #1a73e8;
  margin: 0;
  flex-shrink: 0;
}

.btn-continue {
  width: 100%;
  padding: 14px;
  background: #1a73e8;
  color: #fff;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 0.5px;
  transition: all 0.15s ease;
  box-shadow: 0 3px 8px rgba(26, 115, 232, 0.2);
}

.btn-continue:hover:not(:disabled) {
  background: #1557b0;
  box-shadow: 0 4px 12px rgba(26, 115, 232, 0.35);
}

.btn-continue:disabled {
  background: #c4cad3;
  cursor: not-allowed;
  box-shadow: none;
  color: #888;
}
</style>
